import React, { useEffect } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createRoute, useLocation, useParams, useRouter, useSearch } from '@tanstack/react-router';

import { notifyBugsnag } from '@/components/bugsnag';

import { setRecordName } from './announce';
import { hardNavigate } from './browser';
import DeadShellControls from './DeadShellControls';
import { isServedDocument, shellContextOf } from './documents';
import { flashSnackbar } from './flash';
import ProbePending from './ProbePending';
import { errorStatus } from './queryClient';
import { toRecordNames } from './recordNames';
import {
  fetchRouteProps,
  parseRouteProps,
  RoutePropsContractError,
  routePropsQueryKey
} from './routeProps';
import { useSingleRoot } from './SingleRootContext';

import type { Query } from '@tanstack/react-query';
import type { AnyRoute, AnyRouter, ParsedLocation } from '@tanstack/react-router';
import type { ComponentType } from 'react';

import type { ShellRouterContext } from './documents';
import type { RailsRouteParams, RoutePropsParser } from './routeProps';
import type {
  DeclaredCrumbs,
  RecordNames,
  RouteLeavePolicy,
  SingleRootBootstrap,
  SingleRootParity,
  SingleRootRouteProps,
  SingleRootStaticData
} from './types';

export interface MountRouteContext<TParsedProps extends object = object> {
  params: Record<string, string>;
  search: Record<string, unknown>;
  bootstrap: SingleRootBootstrap;
  /** The page presenter's response, on routes that declare `routeProps`. */
  routeProps?: SingleRootRouteProps;
  /** Validated server props, when the route declares a parser. */
  parsedProps?: TParsedProps;
}

export interface MountRouteOptions<TProps extends object, TParsedProps extends object = TProps> {
  parent: AnyRoute;
  /** The Rails path spec in TanStack syntax, e.g. `/admin/jobs/$jobId`. */
  path: string;
  /** Route identity, `controller_path#action`. */
  railsAction: string;
  /** The page's existing default export, rendered unchanged. */
  component: ComponentType<TProps>;
  /** Props the HAML view passes today, computed from params, search, the bootstrap and route props. */
  props: (context: MountRouteContext<TParsedProps>) => TProps;
  /** A feature schema or parser for the presenter's `props`, applied before the adapter. */
  parse?: RoutePropsParser<TParsedProps>;
  /**
   * For pages whose props are server-computed: the Rails action's path parameters for
   * /api/v4/single_root/route_props. The page renders once they arrive.
   */
  routeProps?: (
    params: Record<string, string>,
    search: Record<string, unknown>
  ) => RailsRouteParams;
  /**
   * The record's name for the title template and the crumbs, and the name of the job it belongs
   * to when that is another record. Defaults to the route props' page title.
   */
  useRecordName?: (context: MountRouteContext<TParsedProps>) => string | RecordNames | null;
  /** Remounts the page when its identity changes (pages that read the URL once at mount). */
  remountKey?: (context: MountRouteContext<TParsedProps>) => string;
  /** Page enhancement scripts whose download errors are reported without blocking the route. */
  assets?: string[];
  /** Defaults to `document`, keeping beforeunload and leave prompts until router blockers. */
  leave?: RouteLeavePolicy;
  title?: SingleRootStaticData['title'];
  breadcrumbs?: DeclaredCrumbs;
  parity: SingleRootParity;
  /** The record the page shows, so the shell can show who else is on it. */
  record?: SingleRootStaticData['record'];
}

const loadedScripts = new Map<string, Promise<void>>();

export const loadScript = async (src: string): Promise<void> => {
  const existing = loadedScripts.get(src);
  if (existing) return existing;
  const promise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      loadedScripts.delete(src);
      reject(new Error(`Failed to load ${src}`));
    };
    document.head.appendChild(script);
  });
  loadedScripts.set(src, promise);
  return promise;
};

interface RoutePropsState<TParsedProps extends object> {
  data?: SingleRootRouteProps;
  parsedProps?: TParsedProps;
  error: unknown;
  ready: boolean;
}

const useNoRouteProps = (_params?: unknown, _search?: unknown): RoutePropsState<never> => ({
  error: null,
  ready: true
});

const routePropsQuery = (railsAction: string, railsParams: RailsRouteParams) => ({
  queryKey: routePropsQueryKey(railsAction, railsParams),
  queryFn: async () => fetchRouteProps(railsAction, railsParams),
  retry: false,
  meta: { skipGlobalErrorSnackbar: true }
});

const createRoutePropsHook = <TParsedProps extends object>(
  railsAction: string,
  railsParamsFor: (
    params: Record<string, string>,
    search: Record<string, unknown>
  ) => RailsRouteParams,
  parse?: RoutePropsParser<TParsedProps>
) => {
  const select = (data: SingleRootRouteProps) => ({
    data,
    parsedProps: parse ? parseRouteProps(railsAction, data, parse).props : undefined
  });
  return (
    params: Record<string, string>,
    search: Record<string, unknown>
  ): RoutePropsState<TParsedProps> => {
    const railsParams = railsParamsFor(params, search);
    const client = useQueryClient();
    const query = routePropsQuery(railsAction, railsParams);
    const { data, error } = useQuery({
      ...query,
      select,
      staleTime: Infinity,
      refetchOnReconnect: 'always',
      enabled: client.getQueryState(query.queryKey)?.status !== 'error'
    });
    return { ...data, error, ready: Boolean(data) };
  };
};

const useRoutePropsPageTitle = <TParsedProps extends object>({
  routeProps
}: MountRouteContext<TParsedProps>): string | null => routeProps?.pageTitle ?? null;

/**
 * Adapter, not a fork: registers a route that renders a page's existing default export with
 * the props its HAML view computes today. Admission rules live in `routeTree.ts`.
 */
export const mountRoute = <TProps extends object, TParsedProps extends object = TProps>({
  assets = [],
  breadcrumbs,
  component: Page,
  leave = 'document',
  parent,
  parse,
  parity,
  path,
  props,
  railsAction,
  record,
  remountKey,
  routeProps,
  title,
  useRecordName = useRoutePropsPageTitle
}: MountRouteOptions<TProps, TParsedProps>): AnyRoute => {
  const preloadedProps = new WeakMap<Query, number>();
  const useRouteProps = routeProps
    ? createRoutePropsHook(railsAction, routeProps, parse)
    : useNoRouteProps;

  const MountedPage = () => {
    const params = useParams<AnyRouter, undefined, false>({ strict: false }) as Record<
      string,
      string
    >;
    const search = useSearch<AnyRouter, undefined, false>({ strict: false }) as Record<
      string,
      unknown
    >;
    const router = useRouter<AnyRouter>();
    const location = useLocation<AnyRouter>();
    const shell = useSingleRoot();
    const client = shellContextOf(router)?.queryClient;
    if (!shell) throw new Error(`mountRoute(${railsAction}) rendered outside the shell`);
    const { bootstrap, notify } = shell;
    const servedDocument = isServedDocument(location, bootstrap.document.href);

    const { data, parsedProps, error, ready } = useRouteProps(params, search);
    useEffect(() => {
      if (!routeProps || !client) return;
      const query = client
        .getQueryCache()
        .find(routePropsQueryKey(railsAction, routeProps(params, search)));
      if (query) preloadedProps.delete(query);
    }, [client, data, params, search]);
    const context: MountRouteContext<TParsedProps> = {
      params,
      search,
      bootstrap,
      routeProps: data,
      parsedProps
    };
    const { jobName = null, recordName } = toRecordNames(useRecordName(context));

    useEffect(() => {
      if (!recordName && !jobName) return;
      setRecordName(router, location.pathname, { recordName, jobName });
    }, [jobName, location.pathname, recordName, router]);

    // The served document's flash is already in the bootstrap; later locations show their own.
    const [flash] = data?.flash ?? [];
    useEffect(() => {
      if (!flash || servedDocument) return;
      notify(flashSnackbar(flash));
    }, [flash, notify, servedDocument]);

    // A record the page would redirect away from: let the server decide, as a document load.
    // A 401 already reloads through the shell client.
    const contractFailure = error instanceof RoutePropsContractError;
    const failed = Boolean(error) && !contractFailure && errorStatus(error) !== 401;
    useEffect(() => {
      if (failed && !servedDocument) hardNavigate(location.href);
    }, [failed, location.href, servedDocument]);
    if (failed && servedDocument) throw new Error(`Route props for ${railsAction} failed to load`);

    if (contractFailure) {
      const legacyUrl = new URL(location.href, window.location.origin);
      legacyUrl.searchParams.set('legacy_ui', '1');
      return (
        <div role="alert">
          <DeadShellControls
            legacyHref={`${legacyUrl.pathname}${legacyUrl.search}${legacyUrl.hash}`}
            message={error.message}
            modeUrl={bootstrap.modeUrls.update}
            turnOff={shell.turnOff}
          />
        </div>
      );
    }

    if (!ready) return <ProbePending />;
    return <Page key={remountKey?.(context)} {...props(context)} />;
  };

  const staticData: SingleRootStaticData = {
    railsAction,
    leave,
    title,
    // Readers that know no record names yet may leave them out.
    breadcrumbs:
      breadcrumbs && ((params, names = toRecordNames(null)) => breadcrumbs(params, names)),
    parity,
    // Absent, not undefined, on the routes that show no record.
    ...(record ? { record } : {})
  };

  return createRoute({
    getParentRoute: () => parent,
    path,
    staticData,
    loaderDeps: routeProps ? ({ search }) => search : undefined,
    loader:
      routeProps || assets.length
        ? async ({
            context,
            params,
            deps,
            location,
            preload,
            route
          }: {
            context: ShellRouterContext;
            params: Record<string, string>;
            deps: Record<string, unknown>;
            location: ParsedLocation;
            preload: boolean;
            route: AnyRoute;
          }) => {
            if (context.admittedRouteFor(location.pathname)?.id !== route.id) return;
            await Promise.all(assets.map(async (src) => loadScript(src).catch(notifyBugsnag)));
            if (routeProps) {
              const query = routePropsQuery(railsAction, routeProps(params, deps));
              const cached = context.queryClient.getQueryData<SingleRootRouteProps>(query.queryKey);
              const entry = context.queryClient.getQueryCache().find(query.queryKey);
              const prefetchedAt = entry && preloadedProps.get(entry);
              const freshPreload =
                prefetchedAt !== undefined &&
                prefetchedAt === entry?.state.dataUpdatedAt &&
                Date.now() - prefetchedAt < 30_000 &&
                !context.queryClient.getQueryState(query.queryKey)?.isInvalidated;
              if (!preload && entry) preloadedProps.delete(entry);
              await context.queryClient
                .ensureQueryData(query)
                .then(async () => {
                  if (cached && !freshPreload && !entry?.isActive())
                    await context.queryClient.fetchQuery(query);
                  const loaded = context.queryClient.getQueryCache().find(query.queryKey);
                  if (!loaded) return;
                  if (preload) preloadedProps.set(loaded, loaded.state.dataUpdatedAt);
                  else preloadedProps.delete(loaded);
                })
                .catch(() => undefined);
            }
          }
        : undefined,
    component: MountedPage
  });
};
